import { CustomHttpInterceptor } from './lib/interceptors/custom.interceptor';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Injector, NgModule } from '@angular/core';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { ToastrModule } from 'ngx-toastr';


import { AccessRequiredGuard } from './auth/guards/access-required.guard';
import { APICGuard } from './auth/guards/apic.guard';
import { CLINICAL_TRIALS_API_URL } from 'projects/labcorp/clinical-trials-ui/src/lib/tokens';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ClinicalTrialsUIModule } from 'projects/labcorp/clinical-trials-ui/src/lib/clinical-trials-ui.module';
import { environment } from '@app/environments/environment';
import { environmentReducer } from '../environments/state/environment.reducer';
import { getEnvironmentValues } from '../environments/environment.utils';
import { IEnvironment } from '../environments/interfaces';
import { LoginComponent } from './auth/components/login/login.component';
import { PaginationEffects } from './lib/pagination/state/pagination.effects';
import { paginationReducer } from './lib/pagination/state/pagination.reducer';
import { PAGINATION_STATE_KEY } from './lib/pagination/pagination.constants';
import { WINDOW_PROVIDERS } from './lib/services/window.service';
import { StudiesEffects } from './portal/studies/state/studies.effects';
import { studiesReducer } from './portal/studies/state/studies.reducer';
import { STUDIES_STATE_KEY } from './portal/studies/studies.constants';
import { SuperuserGuard } from './auth/guards/superuser.guard';
import { Router } from '@angular/router';


@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    ClinicalTrialsUIModule,
    EffectsModule.forRoot([
      PaginationEffects,
      StudiesEffects,
    ]),
    HttpClientModule,
    OktaAuthModule,
    StoreModule.forRoot(
      {
        environment: environmentReducer,
        [PAGINATION_STATE_KEY]: paginationReducer,
        [STUDIES_STATE_KEY]: studiesReducer,
      },
      {
        runtimeChecks: {
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateImmutability: true,
          strictStateSerializability: true
        }
      }
    ),
    environment.reduxDevToolsEnabled ? StoreDevtoolsModule.instrument({
      maxAge: 100,
    }) : [],
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      timeOut: 4000,
    }),
  ],
  providers: [
    AccessRequiredGuard,
    APICGuard,
    SuperuserGuard,
    WINDOW_PROVIDERS,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptor,
      multi: true
    },
    {
      provide: OKTA_CONFIG,
      useFactory: () => {
        const environmentValues: IEnvironment = getEnvironmentValues();
        const oktaAuth = new OktaAuth(environmentValues.okta);

        return {
          oktaAuth,
          onAuthRequired: (injector: Injector) => {
            const router = injector.get(Router);
            // Redirect the user to your custom login page
            router.navigate(['/login']);
          }
        };
      },
      multi: false,
    },
    {
      provide: CLINICAL_TRIALS_API_URL,
      useFactory: () => {
        const environmentValues: IEnvironment = getEnvironmentValues();

        return environmentValues.restAPI;
      },
      multi: false,
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
